import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';

export default function About() {
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const formData = new FormData(form);
    const honeypot = formData.get('company_alt') as string;
    if (honeypot?.trim()) {
      setFormStatus('success');
      setContactForm({ name: '', email: '', message: '' });
      return;
    }
    try {
      const response = await fetch('https://readdy.ai/api/form/d9slrp94ne17ei0sdjk0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          name: contactForm.name,
          email: contactForm.email,
          message: contactForm.message,
        }).toString(),
      });
      const responseText = await response.text();
      if (response.ok) {
        setFormStatus('success');
        setContactForm({ name: '', email: '', message: '' });
      } else {
        setFormStatus('error');
        setFormError(responseText || '提交失败，请稍后再试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请稍后重试');
    }
  };

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '蜜桃网',
    url: 'https://mitaowang.com/',
    description: '蜜桃网是值得信赖的内容聚合与资源发现平台，汇聚海量优质内容，提供便捷高效的一站式浏览体验。',
    foundingDate: '2024',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@mitaowang.com',
      availableLanguage: ['Chinese'],
    },
  };

  const aboutPageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于蜜桃网 - 精选内容聚合平台',
    description: '了解蜜桃网的品牌故事、信誉承诺和联系方式。蜜桃网致力于为用户提供高品质的内容聚合服务。',
    url: 'https://mitaowang.com/about',
    inLanguage: 'zh-CN',
    isPartOf: {
      '@type': 'WebSite',
      name: '蜜桃网',
      url: 'https://mitaowang.com/',
    },
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '蜜桃网是什么？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '蜜桃网是一个中文内容聚合与资源发现平台，汇聚海量优质内容，为用户提供便捷、高效的一站式浏览体验。覆盖科技前沿、生活美学、文化视野、商业洞察、健康养生、旅行探索等多个领域。',
        },
      },
      {
        '@type': 'Question',
        name: '蜜桃网的内容来源是否可靠？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '蜜桃网高度重视内容品质，编辑团队对每一篇内容进行严格审核，确保信息的准确性和可读性。我们只收录经过验证的优质内容，为用户提供值得信赖的阅读体验。',
        },
      },
      {
        '@type': 'Question',
        name: '如何联系蜜桃网？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过官网的联系表单提交问题，或发送邮件至 contact@mitaowang.com。我们的客服团队将在24小时内回复您的咨询。',
        },
      },
      {
        '@type': 'Question',
        name: '蜜桃网的内容多久更新一次？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '蜜桃网每日更新精选内容，确保用户始终能够获取到最新、最热门的资讯和深度文章。编辑团队保持7×24小时的内容监控与更新机制。',
        },
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">
        {JSON.stringify(orgJsonLd)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(aboutPageJsonLd)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(faqJsonLd)}
      </script>
      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Header */}
        <section className="pt-24 pb-10 md:pt-32 md:pb-14 border-b border-background-200/70">
          <div className="w-full max-w-7xl mx-auto px-6 md:px-10">
            <div className="mb-4">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '关于我们' },
                ]}
              />
            </div>
            <div className="mt-6">
              <h1 className="font-heading text-3xl md:text-4xl text-foreground-900 mb-4">
                关于蜜桃网
              </h1>
              <p className="text-lg text-foreground-500 max-w-2xl">
                了解我们的品牌故事、价值观和联系方式
              </p>
            </div>
          </div>
        </section>

        {/* Brand Introduction */}
        <section className="py-14 md:py-20">
          <div className="w-full max-w-7xl mx-auto px-6 md:px-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="w-full h-80 md:h-96 rounded-2xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=A%20warm%20and%20inviting%20modern%20office%20workspace%20with%20creative%20team%20collaboration%2C%20warm%20peach%20and%20cream%20interior%2C%20green%20plants%2C%20wooden%20desks%2C%20soft%20natural%20light%2C%20minimal%20elegant%20design%2C%20professional%20yet%20cozy%20atmosphere%2C%20editorial%20corporate%20photography%20with%20warm%20tones%20and%20gentle%20shadows&width=800&height=600&seq=about-brand-mitao&orientation=landscape"
                    alt="蜜桃网品牌形象"
                    className="w-full h-full object-cover"
                    title="蜜桃网 - 精选内容聚合平台"
                  />
                </div>
              </div>
              <div>
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-6">
                  品牌故事
                </h2>
                <div className="space-y-4 text-foreground-600 leading-relaxed">
                  <p>
                    蜜桃网成立于2024年，是一家专注于中文内容聚合与资源发现的互联网平台。我们的使命是帮助用户在信息海洋中高效地找到真正有价值的优质内容。
                  </p>
                  <p>
                    在这个信息爆炸的时代，每天都有海量的内容被生产和消费。然而，真正有深度、有温度、有见地的好内容往往被淹没在算法推荐和流量至上的喧嚣之中。蜜桃网的诞生，正是为了改变这一现状。
                  </p>
                  <p>
                    我们的编辑团队秉持"品质优先"的理念，以人工甄选结合智能算法的方式，从科技前沿、生活美学、文化视野、商业洞察、健康养生、旅行探索等多个维度，为用户精心筛选和推荐值得阅读的内容。
                  </p>
                  <p>
                    "蜜桃"寓意甜蜜与美好，我们希望通过这个名字传递出温暖、亲近的品牌气质——就像一位值得信赖的朋友，总能在您需要的时候，递上一份精心准备的阅读清单。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Values / Reputation */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-6 md:px-10">
            <div className="text-center mb-14">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-4">
                我们的信誉承诺
              </h2>
              <p className="text-foreground-500 max-w-xl mx-auto">
                诚信、品质、创新，是蜜桃网始终坚守的核心价值观
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="p-8 rounded-2xl bg-background-50 border border-background-200/70 text-center">
                <div className="w-14 h-14 rounded-2xl bg-primary-100 flex items-center justify-center mx-auto mb-5">
                  <i className="ri-shield-check-line text-2xl text-primary-600"></i>
                </div>
                <h3 className="font-heading text-lg text-foreground-900 mb-3">
                  内容真实可靠
                </h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  每篇内容经过严格事实核查与编辑审核，确保信息准确无误。我们杜绝虚假新闻、标题党和低质内容，为读者提供可信赖的信息来源。
                </p>
              </div>
              <div className="p-8 rounded-2xl bg-background-50 border border-background-200/70 text-center">
                <div className="w-14 h-14 rounded-2xl bg-accent-100 flex items-center justify-center mx-auto mb-5">
                  <i className="ri-user-heart-line text-2xl text-accent-600"></i>
                </div>
                <h3 className="font-heading text-lg text-foreground-900 mb-3">
                  用户价值至上
                </h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  一切以用户体验和需求为中心。不追逐流量，不做标题党，不贩卖焦虑。我们致力于创造健康、积极的阅读环境，让每一次点击都有所收获。
                </p>
              </div>
              <div className="p-8 rounded-2xl bg-background-50 border border-background-200/70 text-center">
                <div className="w-14 h-14 rounded-2xl bg-secondary-100 flex items-center justify-center mx-auto mb-5">
                  <i className="ri-lightbulb-line text-2xl text-secondary-600"></i>
                </div>
                <h3 className="font-heading text-lg text-foreground-900 mb-3">
                  持续创新进化
                </h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  不断优化推荐算法和用户体验，探索内容分发的更多可能性。让技术服务于优质内容，而非让内容屈从于流量规则。
                </p>
              </div>
            </div>

            {/* Stats */}
            <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center p-6 rounded-xl bg-background-50 border border-background-200/70">
                <div className="text-3xl font-bold text-primary-500 font-heading mb-1">10万+</div>
                <div className="text-xs text-foreground-500">日均活跃读者</div>
              </div>
              <div className="text-center p-6 rounded-xl bg-background-50 border border-background-200/70">
                <div className="text-3xl font-bold text-accent-500 font-heading mb-1">5000+</div>
                <div className="text-xs text-foreground-500">精选文章库</div>
              </div>
              <div className="text-center p-6 rounded-xl bg-background-50 border border-background-200/70">
                <div className="text-3xl font-bold text-primary-500 font-heading mb-1">6大</div>
                <div className="text-xs text-foreground-500">内容分类板块</div>
              </div>
              <div className="text-center p-6 rounded-xl bg-background-50 border border-background-200/70">
                <div className="text-3xl font-bold text-accent-500 font-heading mb-1">99.7%</div>
                <div className="text-xs text-foreground-500">读者满意度</div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-14 md:py-20">
          <div className="w-full max-w-3xl mx-auto px-6 md:px-10">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-4">
                常见问题
              </h2>
              <p className="text-foreground-500">
                关于蜜桃网，您可能想了解
              </p>
            </div>
            <div className="space-y-4">
              {[
                { q: '蜜桃网是什么？', a: '蜜桃网是一个中文内容聚合与资源发现平台，汇聚海量优质内容，为用户提供便捷、高效的一站式浏览体验。覆盖科技前沿、生活美学、文化视野、商业洞察、健康养生、旅行探索等多个领域。' },
                { q: '蜜桃网的内容来源是否可靠？', a: '蜜桃网高度重视内容品质，编辑团队对每一篇内容进行严格审核，确保信息的准确性和可读性。我们只收录经过验证的优质内容，为用户提供值得信赖的阅读体验。' },
                { q: '如何联系蜜桃网？', a: '您可以通过官网的联系表单提交问题，或发送邮件至 contact@mitaowang.com。我们的客服团队将在24小时内回复您的咨询。' },
                { q: '蜜桃网的内容多久更新一次？', a: '蜜桃网每日更新精选内容，确保用户始终能够获取到最新、最热门的资讯和深度文章。编辑团队保持每日内容监控与更新机制。' },
              ].map((faq, index) => (
                <details key={index} className="group rounded-xl bg-background-50 border border-background-200/70 overflow-hidden cursor-pointer">
                  <summary className="flex items-center justify-between px-6 py-5 text-foreground-800 font-medium text-sm list-none">
                    {faq.q}
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <div className="px-6 pb-5 text-sm text-foreground-500 leading-relaxed">
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-6 md:px-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-4">
                  联系我们
                </h2>
                <p className="text-foreground-500 mb-8">
                  如果您有任何问题、建议或合作意向，欢迎通过以下方式与我们取得联系。我们期待听到您的声音。
                </p>
                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-lg text-primary-600"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-800 mb-1">电子邮箱</div>
                      <div className="text-sm text-foreground-500">contact@mitaowang.com</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-customer-service-2-line text-lg text-accent-600"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-800 mb-1">客服热线</div>
                      <div className="text-sm text-foreground-500">400-888-8888</div>
                      <div className="text-xs text-foreground-400 mt-1">工作日 9:00 - 18:00</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-secondary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-lg text-secondary-600"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-800 mb-1">公司地址</div>
                      <div className="text-sm text-foreground-500">北京市朝阳区建国路88号SOHO现代城A座</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-8 rounded-2xl bg-background-50 border border-background-200/70">
                <h3 className="font-heading text-lg text-foreground-900 mb-6">
                  发送消息
                </h3>
                {formStatus === 'success' ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center mb-4">
                      <i className="ri-check-line text-3xl text-primary-600"></i>
                    </div>
                    <p className="text-foreground-800 font-medium mb-2">消息已发送！</p>
                    <p className="text-sm text-foreground-500">感谢您的留言，我们将在24小时内回复您。</p>
                    <button
                      onClick={() => setFormStatus('idle')}
                      className="mt-6 px-6 py-2 text-sm font-medium rounded-lg border border-background-200 text-foreground-600 hover:text-foreground-900 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      发送新消息
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleContactSubmit} className="space-y-5" data-readdy-form="true">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        您的姓名
                      </label>
                      <input
                        id="contact-name"
                        type="text"
                        name="name"
                        value={contactForm.name}
                        onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                        required
                        className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        电子邮箱
                      </label>
                      <input
                        id="contact-email"
                        type="email"
                        name="email"
                        value={contactForm.email}
                        onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                        required
                        className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        留言内容
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        value={contactForm.message}
                        onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                        placeholder="请输入您想说的内容（最多500字）"
                      />
                      <div className="text-xs text-foreground-400 mt-1 text-right">
                        {contactForm.message.length}/500
                      </div>
                    </div>
                    <input
                      type="text"
                      name="company_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="honeypot-field"
                    />
                    {formStatus === 'error' && (
                      <div className="text-sm text-red-500 bg-red-50 rounded-lg px-4 py-3">
                        {formError || '提交失败，请稍后再试'}
                      </div>
                    )}
                    <button
                      type="submit"
                      className="w-full py-3 text-sm font-medium rounded-lg bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      发送消息
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}